import { useMemo } from "react";
import { DefinedFaultTask } from "../../../utils/scenario-types"
import { isNil } from "lodash";
import CustomToolTip from "../../../../../CommonComponents/CustomToolTip";
import { ClockSvg, GeoFeatureSvg, SunExposerSvg, TargetTrackSvg } from "../../../../../CommonComponents/svgs/Svgs";
import { formatSecondsDuration, replaceUnderscoresWithSpaces } from "../../../../../../utils/commonFunctions";
import { formatExecDurationValue } from "../../../../../CommonComponents/timeline-fault-events/helperFunction";

const GeofeatureType = {
    GEOFENCE: "Geofence",
    TARGET_TRACK: "TargetTrack"
} as const;



const FaultReportDetails = ({ Fault }: { Fault: DefinedFaultTask }) => {

    const execReq = useMemo(() => Fault.executionRequirement, [Fault.executionRequirement]);
    const faultDetails = useMemo(() => Fault.faultDetails ? Fault.faultDetails?.display : null, [Fault.faultDetails]);

    // Memoize computed values to prevent unnecessary re-renders
    const timeOffsetText = useMemo(() => {
        if (isNil(execReq?.timeOffset)) return null;
        return `T₀ + ${execReq.timeOffset} min`;
    }, [execReq?.timeOffset]);

    const execDurationText = useMemo(() => formatExecDurationValue(execReq?.duration), [execReq?.duration]);
    const faultDurationText = useMemo(
        () => formatSecondsDuration(Fault?.duration, { hideZero: true }),
        [Fault?.duration]
    );
    const durationsMatch = useMemo(
        () => Boolean(execDurationText && faultDurationText && execDurationText === faultDurationText),
        [execDurationText, faultDurationText]
    );

    const durationTooltipText = useMemo(() => {
        if (durationsMatch && execDurationText) {
            return execDurationText;
        }
        if (execDurationText && faultDurationText) {
            return `${execDurationText} → ${faultDurationText}`;
        }
        return faultDurationText ?? execDurationText ?? null;
    }, [durationsMatch, execDurationText, faultDurationText]);

    const durationTooltip = useMemo(() => {
        if (!durationTooltipText) return null;
        return `Duration: ${durationTooltipText}`;
    }, [durationTooltipText]);

    return (
        <div className='fault__report__container__body'>

            <div className="report__body__requirment__section">
                <span className="requirment__label">Scheduling requirement</span>

                <div className="requirment__badge__container">
                    {execReq?.geofeatureName && (
                        <CustomToolTip title={execReq.geofeatureName}>
                            <div className="requirment__badge" aria-label={`Geofeature: ${execReq.geofeatureName}`}>
                                {execReq.geofeatureType === GeofeatureType.GEOFENCE ? (
                                    <GeoFeatureSvg />
                                ) : (
                                    <TargetTrackSvg />
                                )}
                                <span className="requirment__badge">
                                    {execReq.geofeatureName}
                                </span>
                            </div>
                        </CustomToolTip>
                    )}
                    {execReq?.sunExposure && (
                        <CustomToolTip title={execReq.sunExposure}>
                            <div className="requirment__badge" aria-label={`Sun Exposure: ${execReq.sunExposure}`}>
                                <SunExposerSvg type={execReq?.exposerType} />
                            </div>
                        </CustomToolTip>
                    )}

                    {/* Time Offset */}
                    {timeOffsetText && (
                        <CustomToolTip title={timeOffsetText}>
                            <div className="requirment__badge" aria-label={timeOffsetText}>
                                {timeOffsetText}
                            </div>
                        </CustomToolTip>
                    )}

                    {/* Duration */}
                    {durationTooltip && (
                        <CustomToolTip title={durationTooltip}>
                            <div className="requirment__badge" aria-label={durationTooltip}>
                                <ClockSvg />
                                {execDurationText && faultDurationText && !durationsMatch ? (
                                    <div className="badge__text__container">
                                        <span className="strike__text">{execDurationText}</span>
                                        <span className="simple__text">{faultDurationText}</span>
                                    </div>
                                ) : (
                                    <span className="simple__text">{faultDurationText ?? durationTooltipText}</span>
                                )}
                            </div>
                        </CustomToolTip>
                    )}
                </div>
            </div>

            {faultDetails &&
                <div className="report__body__configuration__section">
                    <span className="report__body__configuration__label">Configuration</span>
                    <div className="configuration__details__container">
                        {Object.entries(faultDetails).map(([key, value]) => (
                            <div className="details__section" key={key}>
                                <span className="details__label">{replaceUnderscoresWithSpaces(key)}</span>
                                <span className="details__text">{String(value)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            }
        </div >
    )
}

export default FaultReportDetails